import {
  GaugeSettingsType,
  GaugeSettingsSchema,
  GeneratedLabelSettingsSchema,
  GeneratedSegmentSettingsSchema,
} from '~core/components/gauges/gauge-schema'
import {
  downloadGaugeConfig,
  readGaugeConfigFile,
} from '~core/components/gauges/gauge-import-export'
import { z } from 'zod'
import { defaultGaugeSettings } from '~core/components/gauges/gauge-settings'

type SetValueFunction = (key: string, value: any) => void

export const handleExport = (
  formState: Partial<GaugeSettingsType>,
  onSuccess: () => void,
  onError: (error: Error) => void,
) => {
  try {
    // First validate the input config against the schema
    const validatedConfig = GaugeSettingsSchema.parse({
      ...defaultGaugeSettings,
      ...formState,
      // Process special cases that need merging
      backgroundSettings: {
        ...defaultGaugeSettings.backgroundSettings,
        ...formState.backgroundSettings,
      },
      needleSettings: {
        ...defaultGaugeSettings.needleSettings,
        ...formState.needleSettings,
      },
      bezelSettings: {
        ...defaultGaugeSettings.bezelSettings,
        ...formState.bezelSettings,
      },
      generatedLabelSettings: GeneratedLabelSettingsSchema.parse({
        ...defaultGaugeSettings.generatedLabelSettings,
        ...formState.generatedLabelSettings,
      }),
      generatedSegmentSettings: GeneratedSegmentSettingsSchema.parse({
        ...defaultGaugeSettings.generatedSegmentSettings,
        ...formState.generatedSegmentSettings,
      }),
    })

    downloadGaugeConfig(validatedConfig)
    onSuccess()
  } catch (error) {
    console.error('Export error:', error)
    if (error instanceof z.ZodError) {
      const issues = error.issues
        .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
        .join('\n')
      onError(new Error(`Validation failed:\n${issues}`))
    } else {
      onError(error instanceof Error ? error : new Error('Export failed'))
    }
  }
}

export const handleImport = async (
  file: File,
  setValue: SetValueFunction,
  onSuccess: () => void,
  onError: (error: Error) => void,
) => {
  try {
    const importedConfig = await readGaugeConfigFile(file)

    // Validate the imported config against the schema
    const validatedConfig = GaugeSettingsSchema.parse({
      // Start with defaults
      ...defaultGaugeSettings,
      // Override with imported values
      ...importedConfig,
      // Process special cases that need merging
      segments: importedConfig.segments ?? defaultGaugeSettings.segments,
      backgroundSettings: {
        ...defaultGaugeSettings.backgroundSettings,
        ...importedConfig.backgroundSettings,
      },
      needleSettings: {
        ...defaultGaugeSettings.needleSettings,
        ...importedConfig.needleSettings,
      },
      bezelSettings: {
        ...defaultGaugeSettings.bezelSettings,
        ...importedConfig.bezelSettings,
      },
      generatedLabelSettings: GeneratedLabelSettingsSchema.parse({
        ...defaultGaugeSettings.generatedLabelSettings,
        ...importedConfig.generatedLabelSettings,
      }),
      generatedSegmentSettings: GeneratedSegmentSettingsSchema.parse({
        ...defaultGaugeSettings.generatedSegmentSettings,
        ...importedConfig.generatedSegmentSettings,
      }),
    })

    // Update all gauge settings
    Object.entries(validatedConfig).forEach(([key, value]) => {
      setValue(key, value)
    })

    onSuccess()
  } catch (error) {
    console.error('Import error:', error)
    if (error instanceof z.ZodError) {
      const issues = error.issues
        .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
        .join('\n')
      onError(new Error(`Validation failed:\n${issues}`))
    } else {
      onError(
        error instanceof Error
          ? error
          : new Error('Failed to import configuration'),
      )
    }
  }
}
